import { useCallback } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useRbacReadiness } from "@/hooks/useRbacReadiness";
import type { UseBuildingScopeResult } from "@/hooks/buildings/useBuildingScope";
import type { DeniedAction } from "@/lib/permissionDenied";

/** Building pages a member's per-project permissions can put out of reach. */
export type ProjectFeature = "issues" | "drawingCompare" | "modelViewer" | "contacts";

/** The refusal each feature's sidebar item and route guard explains. */
export const PROJECT_FEATURE_DENIED_ACTION: Record<ProjectFeature, DeniedAction> = {
  issues: "viewIssues",
  drawingCompare: "useDrawingViewer",
  modelViewer: "useModelViewer",
  contacts: "viewContacts",
};

/**
 * May the member see `feature` in the current scope?
 *
 * "Current" is the project the header has selected (URL `?project=`, else the
 * remembered one, else the building's only project). With none selected —
 * "All projects" — the pages list whatever the member may see across their
 * projects, so the feature is open when any project in scope allows it and
 * closed only when none does: blocking a member who can see issues in two of
 * three projects because nothing is selected would be wrong.
 */
export function projectFeatureAllowed(
  check: (projectId: number) => boolean,
  selectedProjectId: number | null,
  scopeProjectIds: readonly number[]
): boolean {
  if (selectedProjectId !== null) return check(selectedProjectId);
  return scopeProjectIds.some(check);
}

export interface ProjectFeatureAccess {
  /**
   * False while the answer could still change — permissions, assignments or
   * the building's project list are loading. Callers neither dim nor block
   * until it is true, so a permitted user never sees a refusal flash.
   */
  ready: boolean;
  /** Whether the feature is usable; always true for owners, admins and other non-members. */
  allowed: (feature: ProjectFeature) => boolean;
  /** The project a refusal is about, or null when it is about all of them. */
  projectId: number | null;
}

/**
 * Per-project view access for the issue pages, the drawing and model viewers
 * and Contacts, shared by
 * the sidebar (dimmed items) and ProjectPermissionRouteGuard (blocked URLs) so
 * the two can never disagree. Takes the caller's `useBuildingScope` result
 * rather than calling it again.
 *
 * Only members (subcontractor-only users) are ever refused here: owners and
 * admins pass every has_permission check, and the org-level module grants
 * (VIEW_PM_TOOL / VIEW_DRAWING_COMPARE_TOOL) still hide the items outright.
 */
export function useProjectFeatureAccess(
  scope: Pick<UseBuildingScopeResult, "selectedProjectId" | "projectOptions" | "optionsLoaded">
): ProjectFeatureAccess {
  const perms = usePermissions();
  const { ready: rbacReady } = useRbacReadiness();
  const { selectedProjectId, projectOptions, optionsLoaded } = scope;
  const isMember = perms.isSubcontractorOnly;
  const ready = rbacReady && (!isMember || optionsLoaded);

  const {
    canViewPMOnProject,
    canViewDrawingCompareOnProject,
    canUseModelViewerOnProject,
    canViewContactsOnProject,
  } = perms;
  const allowed = useCallback(
    (feature: ProjectFeature): boolean => {
      if (!isMember || !ready) return true;
      const checks: Record<ProjectFeature, (projectId: number) => boolean> = {
        issues: canViewPMOnProject,
        drawingCompare: canViewDrawingCompareOnProject,
        modelViewer: canUseModelViewerOnProject,
        contacts: canViewContactsOnProject,
      };
      const check = checks[feature];
      return projectFeatureAllowed(
        check,
        selectedProjectId,
        projectOptions.map((option) => option.id)
      );
    },
    [
      isMember,
      ready,
      canViewPMOnProject,
      canViewDrawingCompareOnProject,
      canUseModelViewerOnProject,
      canViewContactsOnProject,
      selectedProjectId,
      projectOptions,
    ]
  );

  return { ready, allowed, projectId: selectedProjectId };
}
